import React, { useState, useEffect } from "react";
import "./ProfilEspace.css";
import UpdateVisiteurService from "../../../../Services/Visiteure/UpdateVisiteurService";
import axios from "axios";

const ProfilEspace: React.FC = () => {
  /* ── Local state ─────────────────────────────── */
  const visiteurId  = localStorage.getItem("VisiteurId")      ?? "";
  const storedName  = localStorage.getItem("VisiteurName")    ?? "";
  const storedTele  = localStorage.getItem("VisiteurTele")    ?? "";
  const storedAdr   = localStorage.getItem("VisiteurAdresse") ?? "";

  const [nomComplet,   setNomComplet]   = useState(storedName);
  const [tele,         setTele]         = useState(storedTele);
  const [adresse,      setAdresse]      = useState(storedAdr);
  const [emailAccount, setEmailAccount] = useState("");
  const [password,     setPassword]     = useState("");

  const [loading,    setLoading]    = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg,   setErrorMsg]   = useState("");

  // Password change request state
  const [newPassword,    setNewPassword]    = useState("");
  const [pwdLoading,     setPwdLoading]     = useState(false);
  const [pwdSuccess,     setPwdSuccess]     = useState(false);
  const [pwdError,       setPwdError]       = useState("");

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── Fetch profile on load ─────────────────── */
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_PHP_APP_URL}/visiteur/${visiteurId}`
        );
        if (res.data?.length > 0) {
          const v = res.data[0];
          setNomComplet(v.Nom_Complet  || "");
          setTele(v.Tele               || "");
          setAdresse(v.Adresse         || "");
          setEmailAccount(v.EmailAccount || "");
          setPassword(v.Password       || "");
          localStorage.setItem("VisiteurName",    v.Nom_Complet || "");
          localStorage.setItem("VisiteurTele",    v.Tele        || "");
          localStorage.setItem("VisiteurAdresse", v.Adresse     || "");
        }
      } catch (err) {
        console.error("Erreur lors du chargement du profil", err);
      }
    };
    if (visiteurId) fetchProfile();
  }, [visiteurId]);

  /* ── Avatar initials ─────────────────────────── */
  const initials = nomComplet
    ? nomComplet.trim().split(" ").map((w) => w[0]).slice(0, 2).join("")
    : "?";

  /* ── Submit ──────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    if (!nomComplet.trim() || !tele.trim() || !adresse.trim()) {
      setErrorMsg("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      setLoading(true);
      await UpdateVisiteurService(visiteurId, {
        Nom_Complet:  nomComplet,
        Tele:         tele,
        Adresse:      adresse,
        EmailAccount: emailAccount,
        ...(password.trim() ? { Password: password } : {}),
      });
      localStorage.setItem("VisiteurName",    nomComplet);
      localStorage.setItem("VisiteurTele",    tele);
      localStorage.setItem("VisiteurAdresse", adresse);
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.message ??
          "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Password change request ───────────────────────── */
  const handlePasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError("");
    if (!newPassword.trim() || newPassword.trim().length < 6) {
      setPwdError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    try {
      setPwdLoading(true);
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/visiteur/${visiteurId}/security-request`,
        { demande: "change-password", new_password: newPassword }
      );
      setPwdSuccess(true);
      setNewPassword("");
      setTimeout(() => setPwdSuccess(false), 4000);
    } catch (err: any) {
      setPwdError(err?.response?.data?.message ?? "Une erreur est survenue.");
    } finally {
      setPwdLoading(false);
    }
  };

  /* ── Shared sub-components ───────────────────── */
  const renderAvatarSection = () => (
    <div className="profil-avatar-section-pvu">
      <div className="profil-avatar-circle-pvu">
        <span>{initials}</span>
      </div>
      <div className="profil-avatar-info-pvu">
        <strong>{nomComplet || "—"}</strong>
        <span>{tele || "—"}</span>
      </div>
    </div>
  );

  const renderErrorBanner = () =>
    errorMsg ? (
      <div className="error-banner-pvu">
        <i className="bi bi-exclamation-triangle-fill" />
        {errorMsg}
      </div>
    ) : null;

  const renderSuccessPopup = (mobile = false) =>
    showSuccess ? (
      <div className="popup-overlay-pvu">
        <div className="popup-box-pvu">
          <div className="popup-icon-pvu">
            <i className="bi bi-check-circle-fill" />
          </div>
          <h4>Modifications enregistrées !</h4>
          <p>
            Vos informations personnelles ont été mises à jour avec succès.
          </p>
          <button
            id={mobile ? "profil-success-close-mobile" : "profil-success-close"}
            className="btn-popup-close-pvu"
            onClick={() => setShowSuccess(false)}
          >
            <i className="bi bi-arrow-right" />
            Continuer
          </button>
        </div>
      </div>
    ) : null;

  /* ── Form fields ─────────────────────────────── */
  const renderFormFields = (suffix = "") => (
    <div className="profil-form-grid-pvu">
      <div className="profil-field-pvu">
        <label className="profil-label-pvu">Nom complet *</label>
        <input
          id={`profil-nom${suffix}`}
          className="profil-input-pvu"
          type="text"
          placeholder="Votre nom complet"
          value={nomComplet}
          onChange={(e) => setNomComplet(e.target.value)}
        />
      </div>

      <div className="profil-field-pvu">
        <label className="profil-label-pvu">Téléphone *</label>
        <input
          id={`profil-tele${suffix}`}
          className="profil-input-pvu"
          type="tel"
          placeholder="06XXXXXXXX"
          value={tele}
          onChange={(e) => setTele(e.target.value)}
        />
      </div>

      <div className="profil-field-pvu full-width-pvu">
        <label className="profil-label-pvu">Adresse *</label>
        <input
          id={`profil-adresse${suffix}`}
          className="profil-input-pvu"
          type="text"
          placeholder="Votre adresse complète"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
        />
      </div>

      <div className="profil-field-pvu full-width-pvu">
        <label className="profil-label-pvu">Email <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optionnel)</span></label>
        <input
          id={`profil-email${suffix}`}
          className="profil-input-pvu"
          type="email"
          placeholder="exemple@email.com"
          value={emailAccount}
          onChange={(e) => setEmailAccount(e.target.value)}
        />
      </div>

      {/* Password field removed from main form — handled separately below */}
    </div>
  );

  const renderSaveButton = (mobile = false) => (
    <div className="profil-actions-pvu">
      <button
        id={mobile ? "profil-save-btn-mobile" : "profil-save-btn"}
        type="submit"
        className="btn-save-pvu"
        disabled={loading}
      >
        {loading ? (
          <span className="spinner-pvu" />
        ) : (
          <i className="bi bi-check2-circle" />
        )}
        {loading
          ? "Enregistrement…"
          : mobile
          ? "Enregistrer"
          : "Enregistrer les modifications"}
      </button>
    </div>
  );

  /* ── MOBILE VIEW ─────────────────────────────── */
  if (screenWidth <= 450) {
    return (
      <>
        <div className="main-content-profil-pvu">
          <button className="back-btn-pvu" onClick={() => window.history.back()}>
            <i className="bi bi-arrow-left-circle" /> Retour
          </button>

          <div className="profil-header-pvu">
            <h3>Mon Profil</h3>
            <h6>Modifiez vos informations personnelles.</h6>
          </div>

          {renderAvatarSection()}
          {renderErrorBanner()}

          <div className="profil-card-pvu">
            <div className="profil-card-title-pvu">
              <i className="bi bi-pencil-square" />
              Informations personnelles
            </div>
            <form onSubmit={handleSubmit}>
              {renderFormFields("-mobile")}
              {renderSaveButton(true)}
            </form>
          </div>
        </div>

        {renderSuccessPopup(true)}
      </>
    );
  }

  /* ── DESKTOP VIEW ────────────────────────────── */
  return (
    <div className="dashboard-profil-pvu">
      <main className="main-content-profil-pvu">
        <button className="back-btn-pvu" onClick={() => window.history.back()}>
          <i className="bi bi-arrow-left-circle" /> Retour
        </button>

        <div className="profil-header-pvu">
          <h3>Mon Profil</h3>
          <h6>Consultez et modifiez vos informations personnelles.</h6>
        </div>

        {renderAvatarSection()}
        {renderErrorBanner()}

        <div className="profil-card-pvu">
          <div className="profil-card-title-pvu">
            <i className="bi bi-pencil-square" />
            Informations personnelles
          </div>
          <form onSubmit={handleSubmit}>
            {renderFormFields("")}
            {renderSaveButton(false)}
          </form>
        </div>

        {/* ── Password change request card ── */}
        <div className="profil-card-pvu" style={{ marginTop: '1.5rem' }}>
          <div className="profil-card-title-pvu">
            <i className="bi bi-shield-lock" />
            Changer le mot de passe
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted-hvu, #888)', marginBottom: '1rem' }}>
            Votre demande sera envoyée à l’administrateur pour validation avant d’être appliquée.
          </p>
          {pwdError && (
            <div className="error-banner-pvu">
              <i className="bi bi-exclamation-triangle-fill" /> {pwdError}
            </div>
          )}
          {pwdSuccess && (
            <div className="error-banner-pvu" style={{ background: '#1a3a1a', borderColor: '#2e7d32', color: '#81c784' }}>
              <i className="bi bi-check-circle-fill" /> Demande envoyée ! En attente de validation.
            </div>
          )}
          <form onSubmit={handlePasswordRequest}>
            <div className="profil-form-grid-pvu">
              <div className="profil-field-pvu full-width-pvu">
                <label className="profil-label-pvu">Nouveau mot de passe *</label>
                <input
                  id="profil-new-password"
                  className="profil-input-pvu"
                  type="text"
                  placeholder="Saisir le nouveau mot de passe"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="profil-actions-pvu">
              <button
                id="profil-pwd-request-btn"
                type="submit"
                className="btn-save-pvu"
                disabled={pwdLoading}
                style={{ background: 'linear-gradient(135deg, #FFC400, #dfb83bff)' }}
              >
                {pwdLoading ? <span className="spinner-pvu" style={{color:"black" , }}/> : <i className="bi bi-send" />}
                {pwdLoading ? "Envoi..." : "Envoyer la demande"}
              </button>
            </div>
          </form>
        </div>
      </main>

      {renderSuccessPopup(false)}
    </div>
  );
};

export default ProfilEspace;